import { useMemo, useState, type FormEvent } from "react";
import { topicComments } from "@/mocks/topic";

type Comment = {
  id: string;
  nickname: string;
  side: "a" | "b";
  content: string;
  time: string;
  likes: number;
};

type Filter = "all" | "a" | "b";

export default function CommentSection({ sideA, sideB }: { sideA: string; sideB: string }) {
  const [comments, setComments] = useState<Comment[]>(topicComments);
  const [filter, setFilter] = useState<Filter>("all");
  const [nickname, setNickname] = useState("");
  const [content, setContent] = useState("");
  const [side, setSide] = useState<"a" | "b">("a");
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [tip, setTip] = useState("");

  const filtered = useMemo(
    () => (filter === "all" ? comments : comments.filter((c) => c.side === filter)),
    [comments, filter]
  );

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = nickname.trim() || "匿名瓜友";
    const text = content.trim();
    if (!text) {
      setTip("说点什么再发吧，别只留个空评论。");
      return;
    }
    setComments((prev) => [
      { id: `c-${Date.now()}`, nickname: name, side, content: text, time: "刚刚", likes: 0 },
      ...prev,
    ]);
    setNickname("");
    setContent("");
    setTip("发布成功，你的观点已经出现在最上面了。");
  };

  const tabs: { id: Filter; label: string }[] = [
    { id: "all", label: "全部评论" },
    { id: "a", label: sideA },
    { id: "b", label: sideB },
  ];

  return (
    <section id="comments" aria-labelledby="comments-title" className="w-full scroll-mt-24 bg-background-100/60 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 id="comments-title" className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              评论区开麦
            </h2>
            <p className="mt-2 text-sm text-foreground-600">共 {comments.length} 条讨论，按最新排序</p>
          </div>

          <div className="flex w-fit gap-1 rounded-full border border-background-300 bg-background-50 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setFilter(tab.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  filter === tab.id ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="text"
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
              placeholder="昵称（可留空，默认匿名瓜友）"
              maxLength={20}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 sm:max-w-[260px]"
            />
            <div className="flex gap-1 rounded-md border border-background-300 bg-background-50 p-1">
              <button
                type="button"
                onClick={() => setSide("a")}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${
                  side === "a" ? "bg-primary-500 text-background-50" : "text-foreground-600"
                }`}
              >
                站 {sideA}
              </button>
              <button
                type="button"
                onClick={() => setSide("b")}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${
                  side === "b" ? "bg-secondary-500 text-background-50" : "text-foreground-600"
                }`}
              >
                站 {sideB}
              </button>
            </div>
          </div>

          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder="说说你的看法。理性讨论，不人身攻击，不泄露隐私。"
            className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />

          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs text-foreground-500">{content.length}/500</span>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-foreground-800"
            >
              <i className="ri-send-plane-line text-base" aria-hidden="true" />
              发布评论
            </button>
          </div>

          {tip && (
            <p className="mt-3 flex items-center gap-2 text-sm text-primary-600" role="status">
              <i className="ri-information-line text-base" aria-hidden="true" />
              {tip}
            </p>
          )}
        </form>

        <ul className="mt-6 space-y-4">
          {filtered.map((comment) => (
            <li key={comment.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-start gap-3">
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-black ${
                    comment.side === "a" ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-800"
                  }`}
                  aria-hidden="true"
                >
                  {comment.nickname.slice(0, 1)}
                </span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="text-sm font-bold text-foreground-900">{comment.nickname}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                        comment.side === "a"
                          ? "bg-primary-50 text-primary-700"
                          : "bg-secondary-50 text-secondary-800"
                      }`}
                    >
                      站 {comment.side === "a" ? sideA : sideB}
                    </span>
                    <span className="text-xs text-foreground-400">{comment.time}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
                  <button
                    type="button"
                    onClick={() => toggleLike(comment.id)}
                    className={`mt-3 flex items-center gap-1.5 text-xs font-medium transition-colors ${
                      liked[comment.id] ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                    }`}
                  >
                    <i className={liked[comment.id] ? "ri-heart-fill text-base" : "ri-heart-line text-base"} aria-hidden="true" />
                    {comment.likes + (liked[comment.id] ? 1 : 0)}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}